import { useState } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useDataStore } from '../stores/dataStore'
import type { Deal, PipelineStage } from '../types'
import { PIPELINE_STAGES, STAGE_LABELS } from '../types'
import PipelineColumn from '../components/pipeline/PipelineColumn'
import DealCard from '../components/pipeline/DealCard'
import AddDealModal from '../components/pipeline/AddDealModal'
import { Button } from '../components/ui'

export default function Pipeline() {
  const { deals, moveDeal } = useDataStore()
  const [activeId, setActiveId] = useState<string | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [addToStage, setAddToStage] = useState<PipelineStage>('lead')

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const activeDeal = activeId ? deals.find((d) => d.id === activeId) : null

  const getDealsByStage = (stage: PipelineStage): Deal[] => {
    return deals.filter((d) => d.stage === stage && d.status === 'active')
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const dealId = active.id as string
    const newStage = over.id as PipelineStage

    if (PIPELINE_STAGES.includes(newStage)) {
      const deal = deals.find((d) => d.id === dealId)
      if (deal && deal.stage !== newStage) {
        moveDeal(dealId, newStage)
      }
    }
  }

  const handleAddDeal = (stage: PipelineStage) => {
    setAddToStage(stage)
    setIsAddModalOpen(true)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <h1 className="text-xl font-semibold text-text-primary">Pipeline</h1>
        <Button variant="primary" onClick={() => handleAddDeal('lead')}>
          + New deal
        </Button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto flex-1 min-h-0 pb-2">
          {PIPELINE_STAGES.map((stage) => (
            <PipelineColumn
              key={stage}
              stage={stage}
              title={STAGE_LABELS[stage]}
              deals={getDealsByStage(stage)}
              onAddDeal={() => handleAddDeal(stage)}
            />
          ))}
        </div>

        <DragOverlay>
          {activeDeal ? <DealCard deal={activeDeal} stage={activeDeal.stage} /> : null}
        </DragOverlay>
      </DndContext>

      <AddDealModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        initialStage={addToStage}
      />
    </div>
  )
}
